import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';

/**
 * Componente para visualização e análise de logs do Docker.
 * 
 * TODO: Melhorias futuras planejadas:
 * - Implementar integração real com a API do Docker para obter logs em tempo real
 * - Adicionar visualização de métricas de desempenho dos containers (CPU, memória, etc.)
 * - Melhorar a interface com temas personalizáveis e modo escuro dedicado
 * - Implementar funcionalidade de download de logs completos em diferentes formatos
 * - Adicionar análise automática de logs para detecção de problemas comuns
 * - Integrar com sistema de monitoramento para correlacionar logs com eventos do sistema
 */
const DockerLogs = () => {
  const [searchParams] = useSearchParams();
  const container = searchParams.get('container');
  const timestamp = searchParams.get('timestamp');
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [timeRange, setTimeRange] = useState('1h'); // 1h, 3h, 6h, 12h, 24h, all
  const [logLevel, setLogLevel] = useState('all'); // all, error, warn, info, debug
  
  useEffect(() => {
    fetchDockerLogs();
  }, [container, timestamp, timeRange, logLevel]);
  
  const fetchDockerLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Em um ambiente real, isso seria uma chamada API para buscar logs do Docker
      // Aqui estamos simulando com dados mockados
      
      // Simular um atraso de rede
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Gerar logs mockados
      const mockLogs = generateMockLogs(container, timestamp);
      
      // Filtrar por nível de log
      const filteredLogs = logLevel === 'all' 
        ? mockLogs 
        : mockLogs.filter(log => log.level === logLevel);
      
      setLogs(filteredLogs);
    } catch (error) {
      console.error('Erro ao buscar logs do Docker:', error);
      toast.error('Erro ao carregar logs do Docker. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const generateMockLogs = (container, timestamp) => {
    const date = timestamp ? new Date(timestamp) : new Date();
    const logs = [];
    
    // Determinar o intervalo de tempo com base no timeRange
    let startTime;
    switch (timeRange) {
      case '1h':
        startTime = new Date(date.getTime() - 60 * 60 * 1000);
        break;
      case '3h':
        startTime = new Date(date.getTime() - 3 * 60 * 60 * 1000);
        break;
      case '6h':
        startTime = new Date(date.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '12h':
        startTime = new Date(date.getTime() - 12 * 60 * 60 * 1000);
        break;
      case '24h':
        startTime = new Date(date.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'all':
      default:
        startTime = new Date(date.getTime() - 24 * 60 * 60 * 1000);
        break;
    }
    
    // Gerar logs com base no container
    if (container === 'investment-db') {
      logs.push(
        { timestamp: new Date(startTime.getTime() + 5 * 60 * 1000), level: 'info', message: 'PostgreSQL database system is ready to accept connections' },
        { timestamp: new Date(startTime.getTime() + 10 * 60 * 1000), level: 'info', message: 'autovacuum launcher started' },
        { timestamp: new Date(startTime.getTime() + 15 * 60 * 1000), level: 'warn', message: 'incomplete startup packet' },
        { timestamp: new Date(startTime.getTime() + 20 * 60 * 1000), level: 'error', message: 'password authentication failed for user "postgres"' },
        { timestamp: new Date(startTime.getTime() + 25 * 60 * 1000), level: 'info', message: 'connection received: host=172.18.0.3 port=49876' },
        { timestamp: new Date(startTime.getTime() + 30 * 60 * 1000), level: 'info', message: 'connection authorized: user=postgres database=investment_db' },
        { timestamp: new Date(startTime.getTime() + 35 * 60 * 1000), level: 'warn', message: 'database "test_db" does not exist' },
        { timestamp: new Date(startTime.getTime() + 40 * 60 * 1000), level: 'error', message: 'relation "users" does not exist at character 15' },
        { timestamp: new Date(startTime.getTime() + 45 * 60 * 1000), level: 'error', message: 'column "total_operation_withdrawals" does not exist' },
        { timestamp: new Date(startTime.getTime() + 50 * 60 * 1000), level: 'info', message: 'ALTER TABLE daily_operations ADD COLUMN total_operation_withdrawals DECIMAL(15, 2) NOT NULL DEFAULT 0' },
        { timestamp: new Date(startTime.getTime() + 55 * 60 * 1000), level: 'info', message: 'statement: COMMIT' }
      );
    } else if (container === 'investment-backend') {
      logs.push(
        { timestamp: new Date(startTime.getTime() + 5 * 60 * 1000), level: 'info', message: 'Server running on port 3001' },
        { timestamp: new Date(startTime.getTime() + 10 * 60 * 1000), level: 'info', message: 'Connected to database successfully' },
        { timestamp: new Date(startTime.getTime() + 15 * 60 * 1000), level: 'info', message: 'GET /api/simulations 200 52.723 ms' },
        { timestamp: new Date(startTime.getTime() + 20 * 60 * 1000), level: 'info', message: 'POST /api/auth/login 200 157.321 ms' },
        { timestamp: new Date(startTime.getTime() + 25 * 60 * 1000), level: 'warn', message: 'Deprecated method used: findById' },
        { timestamp: new Date(startTime.getTime() + 30 * 60 * 1000), level: 'error', message: 'Error fetching details for simulation 9: column "total_operation_withdrawals" does not exist' },
        { timestamp: new Date(startTime.getTime() + 35 * 60 * 1000), level: 'error', message: 'Error: Invalid login: 535 5.7.8 Error: authentication failed' },
        { timestamp: new Date(startTime.getTime() + 40 * 60 * 1000), level: 'info', message: 'GET /api/simulations/9 404 12.156 ms' },
        { timestamp: new Date(startTime.getTime() + 45 * 60 * 1000), level: 'info', message: 'POST /api/admin/test-email 500 1243.897 ms' },
        { timestamp: new Date(startTime.getTime() + 50 * 60 * 1000), level: 'error', message: 'Erro ao enviar email de teste: Error: Invalid login: 535 5.7.8 Error: authentication failed' },
        { timestamp: new Date(startTime.getTime() + 55 * 60 * 1000), level: 'debug', message: 'SMTP connection options: {"host":"smtp.example.com","port":587,"secure":false}' }
      );
    } else if (container === 'investment-frontend') {
      logs.push(
        { timestamp: new Date(startTime.getTime() + 5 * 60 * 1000), level: 'info', message: 'Starting the development server...' },
        { timestamp: new Date(startTime.getTime() + 10 * 60 * 1000), level: 'info', message: 'Compiled successfully!' },
        { timestamp: new Date(startTime.getTime() + 15 * 60 * 1000), level: 'info', message: 'You can now view frontend in the browser.' },
        { timestamp: new Date(startTime.getTime() + 20 * 60 * 1000), level: 'info', message: 'Local:            http://localhost:3000' },
        { timestamp: new Date(startTime.getTime() + 25 * 60 * 1000), level: 'info', message: 'On Your Network:  http://172.18.0.4:3000' },
        { timestamp: new Date(startTime.getTime() + 30 * 60 * 1000), level: 'warn', message: 'Failed to load resource: the server responded with a status of 404 (Not Found)' },
        { timestamp: new Date(startTime.getTime() + 35 * 60 * 1000), level: 'error', message: 'TypeError: prevLogs[type] is not iterable' },
        { timestamp: new Date(startTime.getTime() + 40 * 60 * 1000), level: 'error', message: 'Uncaught (in promise) SyntaxError: Unexpected token < in JSON at position 0' },
        { timestamp: new Date(startTime.getTime() + 45 * 60 * 1000), level: 'warn', message: 'Each child in a list should have a unique "key" prop.' },
        { timestamp: new Date(startTime.getTime() + 50 * 60 * 1000), level: 'info', message: 'Proxy error: Could not proxy request /api/admin/logs from localhost:3000 to .' },
        { timestamp: new Date(startTime.getTime() + 55 * 60 * 1000), level: 'debug', message: 'React DevTools failed to load: Error: Could not connect to React DevTools.' }
      );
    } else {
      logs.push(
        { timestamp: new Date(startTime.getTime() + 30 * 60 * 1000), level: 'info', message: 'Container started' },
        { timestamp: new Date(startTime.getTime() + 35 * 60 * 1000), level: 'info', message: 'No logs available for this container' }
      );
    }
    
    // Adicionar alguns logs próximos ao timestamp fornecido
    if (timestamp) {
      const targetTime = new Date(timestamp);
      logs.push(
        { timestamp: new Date(targetTime.getTime() - 5 * 60 * 1000), level: 'info', message: `Activity detected at ${targetTime.toISOString()}` },
        { timestamp: new Date(targetTime.getTime() - 2 * 60 * 1000), level: 'warn', message: 'Resource usage high' },
        { timestamp: new Date(targetTime.getTime() - 1 * 60 * 1000), level: 'error', message: 'Connection error occurred' },
        { timestamp: targetTime, level: 'error', message: 'Critical error at exact timestamp' },
        { timestamp: new Date(targetTime.getTime() + 1 * 60 * 1000), level: 'info', message: 'System attempting recovery' },
        { timestamp: new Date(targetTime.getTime() + 5 * 60 * 1000), level: 'info', message: 'System recovered successfully' }
      );
    }
    
    // Ordenar logs por timestamp (mais recentes primeiro)
    return logs.sort((a, b) => b.timestamp - a.timestamp);
  };
  
  // Formatar data
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };
  
  // Filtrar logs por texto
  const filteredLogs = () => {
    if (!filter) return logs;
    
    return logs.filter(log => 
      log.message.toLowerCase().includes(filter.toLowerCase())
    );
  };
  
  // Obter a classe CSS para o nível de log
  const getLogLevelClass = (level) => {
    switch (level) {
      case 'error':
        return 'text-danger';
      case 'warn':
        return 'text-warning';
      case 'info':
        return 'text-info';
      case 'debug':
        return 'text-muted';
      default:
        return '';
    }
  };
  
  // Exportar logs
  const exportLogs = () => {
    try {
      const logsToExport = filteredLogs();
      const dataStr = JSON.stringify(logsToExport, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      
      const exportFileDefaultName = `docker_logs_${container}_${new Date().toISOString().slice(0, 10)}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      toast.success('Logs exportados com sucesso.');
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      toast.error('Erro ao exportar logs. Tente novamente.');
    }
  };
  
  return (
    <MainLayout>
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Logs do Docker: {container}</h2>
              <div className="text-muted mt-1">
                Visualize os logs do container {container}
                {timestamp && ` próximos a ${formatDate(timestamp)}`}
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button 
                  className="btn btn-outline-primary d-none d-sm-inline-block" 
                  onClick={exportLogs}
                  disabled={logs.length === 0}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                    <line x1="12" y1="11" x2="12" y2="17" />
                    <polyline points="9 14 12 17 15 14" />
                  </svg>
                  Exportar Logs
                </button>
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={fetchDockerLogs}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                  </svg>
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Filtrar logs..." 
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  {filter && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setFilter('')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-auto">
                <div className="btn-group">
                  <button 
                    type="button" 
                    className={`btn ${logLevel === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLogLevel('all')}
                  >
                    Todos
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${logLevel === 'error' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLogLevel('error')}
                  >
                    Erros
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${logLevel === 'warn' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLogLevel('warn')}
                  >
                    Avisos
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${logLevel === 'info' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLogLevel('info')}
                  >
                    Info
                  </button>
                </div>
              </div>
              <div className="col-auto">
                <select 
                  className="form-select" 
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <option value="1h">Última hora</option>
                  <option value="3h">Últimas 3 horas</option>
                  <option value="6h">Últimas 6 horas</option>
                  <option value="12h">Últimas 12 horas</option>
                  <option value="24h">Últimas 24 horas</option>
                  <option value="all">Todos os logs</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-3">Carregando logs do Docker...</p>
              </div>
            ) : (
              <div className="logs-container">
                {filteredLogs().length === 0 ? (
                  <div className="empty p-4">
                    <div className="empty-img">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                      </svg>
                    </div>
                    <p className="empty-title">Nenhum log encontrado</p>
                    <p className="empty-subtitle text-muted">
                      Não há logs disponíveis para este container ou o filtro aplicado não retornou resultados.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '200px' }}>Timestamp</th>
                          <th style={{ width: '80px' }}>Nível</th>
                          <th>Mensagem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs().map((log, index) => (
                          <tr key={index} className={getLogLevelClass(log.level)}>
                            <td className="text-nowrap">{formatDate(log.timestamp)}</td>
                            <td>
                              <span className={`badge bg-${
                                log.level === 'error' ? 'danger' : 
                                log.level === 'warn' ? 'warning' : 
                                log.level === 'info' ? 'info' : 
                                'secondary'
                              }`}>
                                {log.level}
                              </span>
                            </td>
                            <td>
                              <div className="text-wrap" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                {log.message}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DockerLogs; 